/**
 * SectionErrorBoundary — wraps an individual section of a page so that
 * one broken section (bad data, undefined prop, etc.) shows a small
 * "section failed" pill instead of blanking the entire page.
 *
 * Use this around any chunk of UI that depends on backend data shape
 * we don't fully control — outreach tracker, waiver panel, billing tab,
 * timeline, etc. The CoachErrorBoundary still wraps the whole route as
 * a final catch, but per-section boundaries keep the rest of the page
 * usable when one card has bad data.
 *
 * Sachin 2026-02-28 — added because Quinn Sargent's profile blanked
 * the whole coach dashboard. Now the worst case is one card shows a
 * red error pill while everything else still works.
 */
import { Component } from "react";
import { AlertTriangle } from "lucide-react";

export default class SectionErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error(`[SectionErrorBoundary:${this.props.section || "?"}]`, error, info?.componentStack);
    }
    render() {
        if (!this.state.error) return this.props.children;
        const label = this.props.section || "This section";
        return (
            <div
                data-testid={`section-error-${this.props.section || "unknown"}`}
                className="mx-6 my-3 border border-amber-700/60 bg-amber-950/30 px-3 py-2 flex items-start gap-2 text-amber-100 text-xs"
            >
                <AlertTriangle className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                    <div className="font-semibold">{label} failed to load.</div>
                    <div className="text-amber-200/80 mt-0.5 truncate" title={String(this.state.error?.message || this.state.error)}>
                        {String(this.state.error?.message || this.state.error)}
                    </div>
                </div>
            </div>
        );
    }
}
